/**
 * Runtime Configuration Manager
 * Loads configuration from config.json file at runtime instead of build-time environment variables
 * This allows a single build to work with multiple deployments by changing config.json
 */

let configCache = null;
let configLoadPromise = null;

/**
 * Default configuration values (fallback if config.json is not available)
 */
const defaultConfig = {
  VITE_API_BASE_URL: 'https://localhost:7213/api/v1',
  VITE_SENTRY_DSN: '',
  VITE_API_ORIGIN: 'http://localhost:3001',
  VITE_APP_NAME: 'معاملات',
  VITE_MULTI_INSTANCE_ENABLED: 'false',
  VITE_HUB_URL: '',
  VITE_FILE_BASE_URL: 'https://localhost:7213',
  VITE_MARKET_MAKER_BOT_URL: '',
};

/**
 * Load configuration from config.json file
 * @returns {Promise<Object>} Configuration object
 */
export async function loadConfig() {
  // Return cached config if already loaded
  if (configCache) {
    return configCache;
  }

  // Return existing promise if load is in progress
  if (configLoadPromise) {
    return configLoadPromise;
  }

  // Start loading config
  configLoadPromise = (async () => {
    try {
      const response = await fetch('/config.json', {
        cache: 'no-cache', // Always fetch fresh config
      });

      if (!response.ok) {
        throw new Error(`Failed to load config.json: ${response.status} ${response.statusText}`);
      }

      const config = await response.json();
      
      // Merge with defaults to ensure all keys exist
      configCache = { ...defaultConfig, ...config };
      
      console.log('[Config] Configuration loaded successfully');
      return configCache;
    } catch (error) {
      console.warn('[Config] Failed to load config.json, using default values:', error);
      // Use default config if loading fails
      configCache = { ...defaultConfig };
      return configCache;
    } finally {
      configLoadPromise = null;
    }
  })();

  return configLoadPromise;
}

/**
 * Get a configuration value by key
 * @param {string} key - Configuration key (e.g., 'VITE_API_BASE_URL')
 * @param {string} defaultValue - Optional default value if key doesn't exist
 * @returns {string} Configuration value
 */
export function getConfig(key, defaultValue = '') {
  if (!configCache) {
    console.warn(`[Config] Config not loaded yet. Call loadConfig() first. Key: ${key}`);
    // Return from defaults if available, otherwise return provided default
    return defaultConfig[key] ?? defaultValue;
  }
  return configCache[key] ?? defaultValue;
}

/**
 * Get configuration value as boolean
 * @param {string} key - Configuration key
 * @param {boolean} defaultValue - Optional default value
 * @returns {boolean} Configuration value as boolean
 */
export function getConfigBoolean(key, defaultValue = false) {
  const value = getConfig(key, defaultValue.toString());
  return value === 'true' || value === true;
}

/**
 * Check if config is loaded
 * @returns {boolean}
 */
export function isConfigLoaded() {
  return configCache !== null;
}

/**
 * Get all configuration (for debugging)
 * @returns {Object|null}
 */
export function getAllConfig() {
  return configCache;
}
